import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../../components/ui/button";
import { FormDialog } from "../../../components/ui/form-dialog";
import CommonFormNew from "../../../components/commonFormNew/commonFormNew";
import DataTable from "../../../components/ui/data-table/data-table";
import { MoreVertical, Edit2 } from "lucide-react";
import { createActionsColumn } from "../../../components/ui/data-table/columns";
import { toast, ToastContainer } from 'react-toastify';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import subscriptionService from "../../../services/SubscriptionService";
import Tooltip from "@mui/material/Tooltip";

export default function Subscription() {
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [columns, setColumns] = useState([]);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    originalPrice: "",
    discountedPrice: "",
  });

  const [tableColumns, setTableColumns] = useState([]);

  const fetchSubscriptions = async () => {
    try {
      const res = await subscriptionService.getSubscriptionList();
      const headers = res?.data?.header || [];
      const body = res?.data?.body || [];

      const dynamicColumns = headers
        .filter(({ key }) => key !== "icon")
        .map(({ key, label }) => {
          const column = {
            accessorKey: key,
            header: label,
          };

          // Add specific cell renderers for different fields
          if (key === "name" || key === "duration") {
            column.cell = ({ row }) => (
                <div className="truncate max-w-[300px]">
                  {row.original[key]}
                </div>
            );
          }

          if (key === "originalPrice" || key === "discountedPrice") {
            column.cell = ({ row }) => `₹${parseInt(row.original[key], 10)}`;
          }

          return column;
        });

      const actionsColumn = {
        accessorKey: "actions",
        header: "Action",
        cell: ({ row }) =>
          createActionsColumn([
            {
              label: "Edit",
              onClick: () => handleEdit(row.original),
              icon: <Edit2 />
            },
          ]).cell({ row }),
      };

      setTableColumns([...dynamicColumns, actionsColumn]);
      setSubscriptionData(body);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      toast.error("Failed to fetch subscriptions");
    }
  };

  const handleEdit = async (subscription) => {
    setEditingSubscription(subscription);
    setFormData({
      name: subscription.name,
      originalPrice: parseInt(subscription.originalPrice, 10).toString(),
      discountedPrice: parseInt(subscription.discountedPrice, 10).toString(),
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    try {
      if (!editingSubscription?.id) return;

      const payload = {
        name: formData.name,
        originalPrice: Number(formData.originalPrice),
        discountedPrice: Number(formData.discountedPrice),
      };

      await subscriptionService.updateSubscription(editingSubscription.id, payload);
      toast.success("Subscription plan updated successfully");
      
      setIsEditDialogOpen(false);
      setEditingSubscription(null);
      setFormData({ discountedPrice: "" });
      
      // Refresh the list
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update subscription plan");
    }
  };

  const handleCancel = () => {
    setIsEditDialogOpen(false);
    setEditingSubscription(null);
    setFormData({
      name: "",
      originalPrice: "",
      discountedPrice: "",
    });
  };

  // Field configurations
  const subscriptionFields = [
    {
      name: "name",
      label: "Plan Name",
      type: "input",
      required: true,
      placeholder: "Enter Plan Name",
      maxLength: 20,
    },
    {
      name: "originalPrice",
      label: "Original Price",
      type: "input",
      required: true,
      placeholder: "Enter Original Price",
    },
    {
      name: "discountedPrice",
      label: "Discounted Price",
      type: "input",
      required: true,
      placeholder: "Enter Discounted Price",
    }
  ];

  const isValid = 
    formData.name &&
    formData.originalPrice && !isNaN(formData.originalPrice) &&
    formData.discountedPrice && !isNaN(formData.discountedPrice);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  return (
    <div className="mx-auto">
      <div>
        <DataTable
          columns={tableColumns}
          data={subscriptionData}
          onRowClick={(row) => console.log("Row clicked:", row)}
        />
      </div>

      <FormDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        title="Edit"
        subtitle="Subscription"
        onSubmit={handleUpdate}
        onCancel={handleCancel}
        submitLabel="Update"
        submitDisabled={!isValid}
        hideCancel={true}
      >
        <CommonFormNew
          config={subscriptionFields}
          formData={formData}
          onFormDataChange={setFormData}
        />
      </FormDialog>
      <ToastContainer />
    </div>
  );
}